import type { SupabaseClient } from '@supabase/supabase-js';
import { isRecipeRelevanceTag, RECIPE_ASSESSMENT_TASKS, RECIPE_RELEVANCE_CATEGORIES, type RecipeRelevanceTag } from '../lib/recipe-relevance';
import type { Recipe } from './recipeService';

/** Internal evidence used to order a single response's catalog. */
export interface EntryRecipeRelevance {
  campaign: readonly RecipeRelevanceTag[];
  entry: readonly RecipeRelevanceTag[];
  recipes: Record<string, readonly RecipeRelevanceTag[]>;
}

const specificBySlug: Readonly<Partial<Record<string, RecipeRelevanceTag>>> = {
  case_study: 'customer_story',
  expert_article: 'expert_insight',
  churn_brief: 'churn_risk',
  feature_pulse: 'feature_feedback',
  win_loss: 'purchase_decision',
  deal_brief: 'deal_opportunity',
  issue_radar: 'technical_issue',
  service_recovery: 'service_recovery',
};
const categories = new Set<RecipeRelevanceTag>(RECIPE_RELEVANCE_CATEGORIES);
const tasks = new Set<RecipeRelevanceTag>(RECIPE_ASSESSMENT_TASKS);

/** Fetch only the caller-visible signals for this response and the recipes on its shelf. */
export async function loadEntryRecipeRelevance(
  supabase: SupabaseClient, campaignId: string, responseId: string, recipeIds: string[],
): Promise<EntryRecipeRelevance> {
  const { data, error } = await supabase.rpc('get_entry_recipe_relevance', {
    p_campaign_id: campaignId, p_response_id: responseId, p_recipe_ids: recipeIds,
  });
  if (error) throw new Error(error.message || 'Recipe relevance unavailable');
  const context: EntryRecipeRelevance = { campaign: [], entry: [], recipes: {} };
  const requested = new Set(recipeIds);
  for (const row of data ?? []) {
    if (!Array.isArray(row?.tags)) continue;
    const tags = [...new Set<RecipeRelevanceTag>(row.tags.filter(isRecipeRelevanceTag))];
    if (row.kind === 'entry' && row.source_id === responseId) context.entry = tags;
    if (row.kind === 'campaign' && row.source_id === campaignId) context.campaign = tags;
    if (row.kind === 'recipe' && requested.has(row.source_id)) context.recipes[row.source_id] = tags;
  }
  return context;
}

/** A shared broad category is insufficient when the entry and recipe name different specific subjects. */
function subjectsAlign(recipe: readonly RecipeRelevanceTag[], source: readonly RecipeRelevanceTag[]): boolean {
  const recipeSpecifics = recipe.filter(tag => !categories.has(tag));
  const sourceSpecifics = source.filter(tag => !categories.has(tag));
  if (recipeSpecifics.length && sourceSpecifics.length) return recipeSpecifics.some(tag => sourceSpecifics.includes(tag));
  return recipe.some(tag => source.includes(tag));
}

/** An assessment of the right subject beats a recap of it. A subjectless rubric can serve any intake. */
function profileFit(recipeTags: Set<RecipeRelevanceTag>, signals: readonly RecipeRelevanceTag[], campaignSubjects: readonly RecipeRelevanceTag[] = []) {
  const subjects = signals.filter(tag => !tasks.has(tag));
  // Profiles hold up to four tags, so any specific match beats four broad category matches.
  const topicScore = subjects.reduce((score, tag) => score + (recipeTags.has(tag) ? (categories.has(tag) ? 1 : 5) : 0), 0);
  const sharedTasks = signals.filter(tag => tasks.has(tag) && recipeTags.has(tag)).length;
  if (!sharedTasks) return { paired: false, score: topicScore };
  const recipeSubjects = [...recipeTags].filter(tag => !tasks.has(tag));
  if (!recipeSubjects.length) return { paired: false, score: sharedTasks * 2 };
  const paired = subjectsAlign(recipeSubjects, subjects.length ? subjects : campaignSubjects);
  return { paired, score: paired ? topicScore + sharedTasks * 2 : topicScore };
}

/** Rank on entry evidence first, then campaign intent, preserving catalog order when evidence ties. */
export function rankResponseRecipes<T extends Pick<Recipe, 'id' | 'isBuiltin' | 'category' | 'slug'>>(
  catalog: readonly T[], context: EntryRecipeRelevance,
): T[] {
  const campaignSubjects = context.campaign.filter(tag => !tasks.has(tag));
  return catalog.map(recipe => {
    const tags = new Set(context.recipes[recipe.id]);
    if (recipe.isBuiltin) {
      if (isRecipeRelevanceTag(recipe.category)) tags.add(recipe.category);
      const specific = recipe.slug && specificBySlug[recipe.slug];
      if (specific) tags.add(specific);
    }
    return { recipe, entry: profileFit(tags, context.entry, campaignSubjects), campaign: profileFit(tags, context.campaign) };
  }).sort((a, b) => Number(b.entry.paired) - Number(a.entry.paired) || b.entry.score - a.entry.score
    || Number(b.campaign.paired) - Number(a.campaign.paired) || b.campaign.score - a.campaign.score)
    .map(item => item.recipe);
}
